'use client';

import React, { useEffect } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { getSearchParamsClient } from '@/app/components/search/common/getSearchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { getPageTypeFromSearchParams } from '@/app/common/urlParametersUtils.ts';
import { DebugInfo } from '@/app/components/search/components/Banners/Debug/styled.ts';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import { hasDebugUserId, isDebugValuesEnabled } from '@/app/common/debug/debugValues.ts';
import { useDebugValues } from '@/app/components/common/DebugValues.tsx';
import { UserRoleDomain } from '@lmn/nextjs-appfw/dist/casper/constants';
import { getUserLoyaltyTier, isLoyaltyUser } from '@/app/common/Loyalty/utils.ts';

/**
 * The user id is stamped on every StarTrack event from the Casper MPT, so a
 * comparison of the id the results page carried against the id the detail page
 * carried is how we tell whether a journey kept one identity or was handed a new
 * one on navigation — the thing behind the app Apex-calendar analysis (MPH-1086).
 *
 * The blue debug panel already shows the id, but only to a signed-in viewer;
 * listing `userId` in the `debug_values` toggle reveals it without a sign-in so
 * the id can be read from a plain app or browser session, and the log below
 * emits the same fields so the two pages can be correlated after the fact
 * rather than read off the screen.
 */

/**
 * The one line the panels add: the user id, no sign-in. Shown only when
 * `debug_values` lists `userId`, so a toggle set for some other reason does not
 * put an id on the screen — the id is the most sensitive thing the panel can
 * show, and it is opted into by name.
 *
 * The panels around it still only open on `debug=true` (or a telesales session),
 * so listing `userId` shows the id to whoever already asked for debug — not to
 * ordinary visitors of a profile the toggle happens to be set for. (MPH-1086)
 */
export const DebugUserIdLine = () => {
  const { commonData, localization } = usePageWrapperData();
  if (!hasDebugUserId(localization?.configValuesApp?.debugValues)) return null;
  return (
    <DebugInfo>
      User Id: <strong>{commonData?.userUid || '—'}</strong>
    </DebugInfo>
  );
};

/**
 * The view keys this document has already emitted a row for.
 *
 * Module scope, deliberately, and this is the whole fix for the repeat emissions measured on
 * 2026-09-22 (6,646 rows for 4,647 views — 30.1% repeats, 85.5% of them results → detail → back
 * oscillation). Neither of the two obvious homes works:
 *
 *  - A single `useRef` holding only the most recent key (what this was) cannot tell "the view
 *    changed" from "the view came back", so every A → B → A step re-emits A.
 *  - A ref holding a Set fixes that in principle but not here, because the component does not
 *    survive the navigation it is meant to measure. `NavigationManager` returns two structurally
 *    different trees depending on `evolutionResultsOnlyEnabled` (a `StickyNavProvider` root for the
 *    evo results step, a fragment otherwise), and `isEvoLayout` flips exactly on results ↔ detail
 *    with the flags as they run in production. React therefore unmounts and remounts this subtree
 *    on that navigation, and any state or ref inside it is lost. That remount is also the readiest
 *    explanation for the 290 same-key-twice rows the split could not attribute to oscillation: the
 *    same flip happens with an unchanged view key whenever a client-side refetch of the config
 *    re-resolves the evo flags (the case MPH-1123 latched `appShowCsw` against), and a full
 *    document load of the same view — the map-view pill and the language switch both do one — is
 *    the other half.
 *
 * Scope is per-document, not per-journey. Per-journey would mean persisting the keys
 * (sessionStorage), which buys only the reload case and costs a store that a webview may refuse or
 * clear, failing silently and taking the whole log with it. Per-document keeps the guard in memory
 * where it cannot fail, kills the oscillation outright, and leaves a genuine reload re-emitting by
 * design — downstream already counts distinct views, so a reload reads as what it is rather than as
 * an inflated rate.
 *
 * Bounded so a long-lived app document cannot grow it without limit; eviction is oldest-first and
 * the cap is far above any real session's distinct-view count.
 */
const emittedViewKeys = new Set<string>();
const MAX_EMITTED_VIEW_KEYS = 100;

const rememberViewKey = (viewKey: string) => {
  if (emittedViewKeys.size >= MAX_EMITTED_VIEW_KEYS) {
    const oldest = emittedViewKeys.values().next().value;
    if (oldest !== undefined) emittedViewKeys.delete(oldest);
  }
  emittedViewKeys.add(viewKey);
};

/**
 * Renders nothing; emits one log per *view* carrying the ids, the Apex flags and
 * everything `debug_values` listed,
 * so a results view and the detail view that follows it can be joined on the user
 * id / search id / hotel id and checked for where the Apex calendar (`apexEnabled`)
 * drops. Mounted once inside the page providers and gated on the toggle alone, so
 * it covers web sessions as well as the app; `isApp` travels in the row to tell
 * them apart. Not gated on `debug=true`, so it covers real sessions, which do not
 * carry the debug flag. (MPH-1086)
 *
 * Everything is read from the live URL, not the server `searchParams`: an in-app
 * results→detail click is a client-side `pushState`, so `window.location` is
 * already the detail URL by the time this re-renders (it is a sibling of the page
 * content in `PageWrapperClient`, so a `setPageType` re-renders it). A per-view
 * key drives re-emission, because a once-per-document guard would drop the detail
 * row entirely; the keys already emitted live in `emittedViewKeys` above, outside
 * the component, because the component itself is remounted by that navigation.
 */
export const DebugUserIdLog = () => {
  const { commonData, localization, searchInfoData, searchParams: searchParamsServer } = usePageWrapperData();
  const enabled = isDebugValuesEnabled(localization?.configValuesApp?.debugValues);
  const debugValues = useDebugValues();

  const searchParams = getSearchParamsClient(searchParamsServer);
  // Resolved, not read raw: the results page carries no `pageType` until a client-side search
  // writes one, and an unresolved value would both label that row `null` and change the view
  // key when the param appears, emitting a second row for the same view.
  const pageType = getPageTypeFromSearchParams(searchParams);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const internalHotelId = pathname.match(/\/s\/tsx\/(\d+)/)?.[1] ?? null;
  const searchId = searchInfoData?.searchId || null;
  const isLiveSearch = searchInfoData?.isLiveSearch ?? null;
  const viewKey = `${pageType}|${searchId}|${internalHotelId}`;

  useEffect(() => {
    if (!enabled || emittedViewKeys.has(viewKey)) return;
    rememberViewKey(viewKey);

    const { configValuesApp } = localization;
    const analysis = {
      pageType,
      userUid: commonData?.userUid || null,
      searchId,
      clickId: searchParams[URLParameters.CLICK_ID] ?? null,
      vcSearchId: searchParams[URLParameters.VC_SEARCH_ID] ?? null,
      internalHotelId,
      businessProfile: localization?.businessProfile ?? null,
      businessProfileIdFromUrl: searchParams[URLParameters.BUSINESS_PROFILE_ID] ?? null,
      isApp: commonData?.isApp ?? null,
      // Customer sign-in, from the login service (`getLoginValuesV2`). This is the one the site
      // itself runs on: every product path reads it, and it is the `loggedIn` the memsearch calls
      // carry. Reads false for everyone while the `bypass_login_call` config is on.
      loggedIn: commonData?.authResponse?.loggedIn ?? null,
      // A different thing that also means "signed in": the NextAuth session on tsx-ui itself, read
      // only by the two debug-visibility helpers (`debugUtils`, `useDebugVisibility`). It is the
      // staff sign-in to this app, not the customer's account, so the two disagreeing is normal and
      // not by itself a finding — they answer different questions.
      //
      // Neither of them reaches the A/B perimeter, which is the question cux_4024 needed answered:
      // the perimeters call hardcodes `loggedIn: false` (createSearchPerimetersAPIUrl) and SAAB
      // allocates on the MPT user id plus the returned perimeters, so no variant split can follow
      // sign-in state. Both are logged so that a variant that *looks* like it tracks sign-in can be
      // shown not to.
      authenticatedNextAuth: commonData?.authenticated ?? null,
      // Employee, as the login service reports it — what the loyalty banners suppress on.
      isEmployee: commonData?.authResponse?.isEmployee ?? null,
      // Employee, as Casper reports it in the MPT `urd` claim — the one `getIsPinkPeopleEnabled`
      // gates on, so it is the one that decides whether employee rates were in the results. Logged
      // as the comparison only: `getUserRoleDomain` hands back the decoded claim without validating
      // it against the enum, so the raw value is not a closed set at runtime and stays out.
      isEmployeeCasperRole: commonData?.userRoleDomain === UserRoleDomain.EMPLOYEE,
      // Whether this was a live (dated) search rather than a month/anytime/country one. Several
      // perimeter conditions turn on it — Pink People and the flexible-dates panel both require it.
      isLiveSearch,
      // Loyalty (the PRO programme) membership, derived the same way the product does it: the
      // Casper profile-manager URN carries a `level_N` tier. Boolean plus the tier, which is a
      // closed enum (1, 2, 3) in `Loyalty/utils`; the URN itself is a catalogue code, not a closed
      // set, so it stays out. Independent of `loyaltyEnabled`, which only gates the display.
      loyaltyEnrolled: commonData ? isLoyaltyUser(commonData) : null,
      loyaltyTier: commonData ? (getUserLoyaltyTier(commonData) ?? null) : null,
      // Whether the results-page Apex products ran — i.e. whether the flexible-dates panel could show.
      apexEnabledForResults: configValuesApp?.apexEnabledForResults ?? null,
      // Whether the flexible-dates panel itself was rendered on the results page.
      apexFlexibleDatesEnabled: configValuesApp?.apexFlexibleDatesEnabled ?? null,
      // The resolved Apex-calendar gate on the detail page (page-independent since MPH-1086).
      // Named after the config field it carries: `configValuesApp.apexCalendarEnabled` is a
      // different value — the raw apex_calendar_enabled toggle, one input to this gate.
      apexEnabled: configValuesApp?.apexEnabled ?? null,
      // Whatever `debug_values` listed, resolved the same way the panel resolves it, as a flat
      // name -> value map so a row stays greppable. The app is the case that cannot be read off a
      // screen, so the listed names have to travel with the log rather than only render.
      // `userId` is not in here: the resolver reserves it and it is already `userUid` above.
      debugValues: Object.fromEntries(debugValues.map(({ name, value }) => [name, value])),
    };

    clientAppLog({
      level: 'INFO',
      codeSourceName: 'debugUserId',
      logMessage: JSON.stringify(analysis),
      url: window?.location?.href,
    });
  }, [
    enabled,
    viewKey,
    pageType,
    searchId,
    isLiveSearch,
    internalHotelId,
    searchParams,
    commonData,
    localization,
    debugValues,
  ]);

  return null;
};
